import { useEffect, useRef, useState } from "react";
import type { ManagementSessionState } from "../../lib/manage-session";
import { AppLink, workspacePath } from "../../lib/navigation";
import { posthog } from "../../lib/posthog";
import { startGoogleSignIn } from "../../lib/auth-client";
import {
  createPendingPublishId,
  discardPendingPublishDraft,
  savePendingPublishDraft,
  type ProjectFlowDraft,
} from "../../lib/pending-publish";
import { ManagementGate } from "../auth/ManagementGate";
import { PricingSection } from "../pricing/PricingSection";
import { ProjectFlow } from "../projects/ProjectFlow";
import { BuildProgress } from "../projects/BuildProgress";
import { DoorwayBrandIcon } from "./DoorwayBrandIcon";
import { MarketingNavigation } from "./MarketingChrome";

export function PublicLandingPage({ session, onRetry = () => undefined }: { session: ManagementSessionState; onRetry?: () => void }) {
  const workspace = session.status === "ready" ? session.workspaces?.[0] : undefined;

  return (
    <div className="app-shell landing-shell oasis-landing">
      <header className="site-header landing-site-header">
        <AppLink className="wordmark" href="/" aria-label="Doorway home">
          <DoorwayBrandIcon className="oasis-wordmark-tile" name="logo-tile-green" />
          Doorway
        </AppLink>
        <nav className="header-actions" aria-label="Main navigation">
          <MarketingNavigation onHomePage />
          {session.status === "ready" ? (
            <AppLink className="demo-pill workspace-pill" href={workspacePath(workspace?.id)}>{workspace?.name ?? "Your workspace"}</AppLink>
          ) : session.status === "checking" ? (
            <span className="demo-pill account-checking" aria-label="Checking account">Checking account</span>
          ) : (
            <AppLink className="header-link landing-sign-in" href="/manage">Sign in</AppLink>
          )}
          <AppLink className="primary-button header-cta" href="/manage">Publish your app <span aria-hidden="true">↓</span></AppLink>
        </nav>
      </header>

      <main>
        <section className="landing-hero" id="publish">
          <div className="landing-hero-copy">
            <p className="eyebrow"><span className="landing-live-dot" aria-hidden="true" />Made for people who build with AI</p>
            <h1>You built it.<br /><span>Now share it.</span></h1>
            <p className="hero-intro">Turn the app folder on your Mac into a safe link your team can use. Doorway handles the parts between “it works for me” and “everyone can open it.”</p>
            <div className="landing-actions">
              <AppLink className="primary-button" href="/manage" onClick={() => posthog.capture("start_free_clicked")}>Publish your app <span aria-hidden="true">↓</span></AppLink>
              <a className="secondary-button" href="/agents">Start with an agent</a>
            </div>
            <div className="landing-proof" aria-label="Product requirements">
              <span>No Git</span><i /><span>No terminal</span><i /><span>No credit card</span>
            </div>
          </div>
          <LandingPublisherFlow session={session} onRetry={onRetry} />
        </section>

        <section className="landing-intent">
          <div>
            <p>Built for the moment when</p>
            <strong>“How do I give this to everyone else?”</strong>
            <span>becomes the only thing standing in the way.</span>
          </div>
        </section>

        <section className="landing-product" id="product" aria-labelledby="landing-product-heading">
          <div className="landing-section-heading">
            <p className="eyebrow">One team system</p>
            <h2 id="landing-product-heading">Share the app.<br />Keep the workflow.</h2>
            <p>Apps give your teammates something useful. Skills give their AI agents a repeatable way to work. Doorway keeps both close to the people who made them.</p>
          </div>

          <div className="landing-product-sections">
            <section className="landing-product-feature landing-product-skills" aria-labelledby="landing-skills-heading">
              <div className="landing-feature-copy">
                <p className="landing-product-label"><span aria-hidden="true">✧</span>Skills</p>
                <h3 id="landing-skills-heading">Your best work, ready to reuse.</h3>
                <p>Share the instructions and resources behind a good result. Teammates can install an approved version without chasing files or setup notes.</p>
                <ul>
                  <li><span>✓</span>Clear capability review</li>
                  <li><span>✓</span>Versions and ownership</li>
                  <li><span>✓</span>Updates that preserve local edits</li>
                </ul>
                <a className="landing-feature-link" href="/library">Explore the public Skills Library <span aria-hidden="true">↗</span></a>
              </div>
              <SkillPreview />
            </section>

            <section className="landing-product-feature landing-product-apps" aria-labelledby="landing-apps-heading">
              <div className="landing-feature-copy">
                <p className="landing-product-label"><span className="landing-app-symbol" aria-hidden="true" />Apps</p>
                <h3 id="landing-apps-heading">A link your team can open.</h3>
                <p>Choose the folder you already have. Doorway checks for private files, publishes a safe copy, and gives you one link to share.</p>
                <ul>
                  <li><span>✓</span>Keeps local files and secrets out</li>
                  <li><span>✓</span>Watches your folder for updates</li>
                  <li><span>✓</span>Supports private team access</li>
                </ul>
              </div>
              <AppStatusPreview />
            </section>
          </div>
        </section>

        <section className="landing-how" id="how-it-works" aria-labelledby="landing-how-heading">
          <div className="landing-section-inner">
            <div className="landing-section-heading landing-heading-compact">
              <p className="eyebrow">From one Mac to the whole team</p>
              <h2 id="landing-how-heading">Three steps.</h2>
            </div>
            <div className="landing-steps">
              <StepCard number="1" title="Choose the folder" copy="Start where your finished app already lives. Doorway watches it with read-only access." visual="folder" />
              <StepCard number="2" title="Review the safe copy" copy="See what will go live, what stays local, and anything that needs your attention." visual="review" />
              <StepCard number="3" title="Share the link" copy="Open it to the public, protect it with a password, or invite your team." visual="share" />
            </div>
          </div>
        </section>

        <section className="landing-security" id="security" aria-labelledby="landing-security-heading">
          <DoorwayBrandIcon className="landing-security-icon" name="door-outline-green" />
          <div><p className="eyebrow">Safe by default</p><h2 id="landing-security-heading">Your private files stay private.</h2></div>
          <p>Doorway scans before it publishes. Files like <code>.env</code>, local credentials, and project history stay on your computer. Every new version waits for your review.</p>
          <a href="/how-it-works">See how publishing works <span aria-hidden="true">↗</span></a>
        </section>

        <PricingSection variant="oasis" />

        <section className="landing-closing" aria-labelledby="landing-closing-heading">
          <DoorwayBrandIcon className="landing-closing-icon" name="door-solid-green" />
          <p>Someone on your team already built the useful thing.</p>
          <h2 id="landing-closing-heading">Open the Doorway.</h2>
          <AppLink className="primary-button" href="/manage">Start free <span aria-hidden="true">↗</span></AppLink>
        </section>
      </main>

      <footer className="landing-footer">
        <a className="footer-wordmark" href="/">Doorway</a>
        <p>How marketing teams ship and share what they build with AI.</p>
        <nav className="footer-links" aria-label="Footer navigation"><a href="#product">Product</a><a href="/library">Skills library</a><a href="/agents">Agents</a><a href="/how-it-works">How it works</a><a href="/docs/quickstart">Quickstart</a><a href="#security">Security</a><a href="/blog">Blog</a></nav>
      </footer>
    </div>
  );
}

function LandingPublisherFlow({ session, onRetry }: { session: ManagementSessionState; onRetry: () => void }) {
  return (
    <div className="landing-publisher" aria-label="Publish an app with Doorway">
      <div className="landing-publisher-titlebar">
        <div className="landing-traffic" aria-hidden="true"><i /><i /><i /></div>
        <strong><DoorwayBrandIcon className="landing-publisher-logo" name="logo-tile-green" />Publish an app</strong>
        <span><i />Stays local until review</span>
      </div>
      <div className="landing-publisher-body">
        <ProjectFlow
          initialWorkspaces={session.status === "ready" ? session.workspaces ?? [] : []}
          renderReviewGate={(draft) => session.status === "ready"
            ? <ManageDraftHandoff draft={draft} />
            : <ManagementGate state={session} onRetry={onRetry} onSignIn={() => saveDraftAndSignIn(draft)} />}
        />
      </div>
      <div className="landing-publisher-footer"><span className="landing-lock" aria-hidden="true" />Doorway checks locally, then opens the app for review and publishing.</div>
    </div>
  );
}

function ManageDraftHandoff({ draft }: { draft: ProjectFlowDraft }) {
  const draftIdRef = useRef(createPendingPublishId());
  const startedRef = useRef(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const returnPath = pendingPublishPath(draftIdRef.current);
    void savePendingPublishDraft(draftIdRef.current, draft)
      .then(() => window.location.assign(returnPath))
      .catch((caught: unknown) => {
        startedRef.current = false;
        setError(caught instanceof Error ? caught.message : "Doorway could not open this folder in the app.");
      });
  }, [attempt, draft]);

  return (
    <section className="flow-card" aria-live="polite">
      <div className="step-label">Opening Doorway</div>
      <h2>Your safe review is ready</h2>
      {error ? (
        <>
          <p className="error-message" role="alert">{error}</p>
          <button className="secondary-button" type="button" onClick={() => { setError(""); setAttempt((value) => value + 1); }}>Try again</button>
        </>
      ) : <BuildProgress label="Opening this folder in the app…" />}
    </section>
  );
}

async function saveDraftAndSignIn(draft: ProjectFlowDraft): Promise<void> {
  const id = createPendingPublishId();
  const returnPath = pendingPublishPath(id);
  await savePendingPublishDraft(id, draft);
  try {
    await startGoogleSignIn({ returnPath });
  } catch (error) {
    await discardPendingPublishDraft(id).catch(() => undefined);
    throw error;
  }
}

function pendingPublishPath(id: string): string {
  return `/manage?publish=${encodeURIComponent(id)}`;
}

function SkillPreview() {
  return (
    <div className="landing-skill-preview" aria-label="Competitor brief skill preview">
      <div className="landing-preview-title"><span className="landing-skill-icon" aria-hidden="true">✧</span><div><strong>Competitor brief</strong><small>Builds a one-page competitor summary from a URL</small></div><span className="landing-status"><i />Team live · v4</span></div>
      <div className="landing-preview-box"><small>WHAT THIS SKILL CAN DO</small><div><span className="safe">Content only</span><span>No scripts</span><span>No network access</span></div></div>
      <div className="landing-preview-box landing-versions"><small>VERSIONS</small><div><i className="live" /><strong>v4</strong><span>You · edited tone guide</span><time>Jul 18</time></div><div><i /><strong>v3</strong><span>You · added example</span><time>Jul 11</time></div></div>
    </div>
  );
}

function AppStatusPreview() {
  return (
    <div className="landing-app-preview" aria-label="Publishing status preview">
      <div className="landing-app-preview-heading"><span className="landing-project-icon" aria-hidden="true"><i /></span><div><strong>pricing-calculator</strong><small>~/Projects/pricing-calc</small></div></div>
      <div className="landing-app-steps"><div><i>✓</i><strong>Watching folder</strong><small>12 files changed</small></div><span /><div><i>✓</i><strong>Safe snapshot</strong><small>.env stays local</small></div><span /><div className="current"><i>3</i><strong>Draft on Doorway</strong><small>Not live yet</small></div></div>
      <div className="landing-review-banner"><div><strong>Draft ready to review</strong><small>Publishing happens in your browser.</small></div><AppLink href="/manage">Review &amp; publish ↗</AppLink></div>
    </div>
  );
}

function StepCard({ number, title, copy, visual }: { number: string; title: string; copy: string; visual: "folder" | "review" | "share" }) {
  return (
    <article className="landing-step-card">
      <span>{number}</span>
      <div className={`landing-step-visual landing-step-${visual}`} aria-hidden="true">
        {visual === "share" ? <DoorwayBrandIcon className="landing-step-door-icon" name="door-solid-green" /> : <i />}
        <i /><i />
      </div>
      <h3>{title}</h3>
      <p>{copy}</p>
    </article>
  );
}
